import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingModal from '../components/OnboardingModal';
import { Heart, Mail, Lock, User, Phone, Eye, EyeOff, Sparkles, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: '' // No default - must be explicitly selected
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false); // Changed to false - don't show by default
  const [selectedRole, setSelectedRole] = useState(null);

  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Validate role is selected from form (not the skip/manual flag)
    const userRole = formData.role; // Only use the actual role from dropdown
    if (!userRole || userRole === 'skip' || userRole === 'manual') {
      setError('Please select your role (Pet Owner or Caregiver)');
      setLoading(false);
      return;
    }

    try {
      await signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: userRole,
        displayName: formData.name,
        avatar: '',
        location: '',
        isVerified: false,
        onboardingCompleted: false
      });
      
      // Redirect based on user role (to profile as default)
      if (userRole === 'caregiver') {
        navigate('/caregiver-profile');
      } else if (userRole === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/owner-profile');
      }
    } catch (err) {
      console.error('Signup error:', err);
      // Show more helpful error message
      if (err.message && err.message.includes('email-already-in-use')) {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.message && err.message.includes('invalid-email')) {
        setError('Invalid email format.');
      } else if (err.message && err.message.includes('weak-password')) {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = (onboardingData) => {
    if (!onboardingData) {
      setShowOnboarding(false);
      return;
    }

    // Extract role from onboarding data
    let role = '';
    if (onboardingData && onboardingData.responses && onboardingData.responses.user_role) {
      // Check if role was selected in onboarding
      if (onboardingData.responses.user_role && onboardingData.responses.user_role.length > 0) {
        role = onboardingData.responses.user_role[0];
      } else if (onboardingData.role) {
        role = onboardingData.role;
      }
    }
    
    // Only close onboarding if role is selected
    if (role) {
      setSelectedRole(role);
      setFormData(prev => ({ ...prev, role }));
      setShowOnboarding(false);
    } else {
      // If no role selected, show error or keep onboarding open
      setError('Please select whether you are a Pet Owner or Caregiver');
    }
  };

  return (
    <>
      <div className="min-h-screen flex pt-[4.25rem] box-border">
        {/* Left Panel - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-8">
            {/* Header - No logo, cleaner */}
            <div className="text-center">
              <h2 className="pf-display text-3xl text-[var(--pf-ink)]">Create your account</h2>
              <p className="mt-2 text-[var(--pf-ink-muted)]">
                Where every pet finds their perfect match
              </p>
              <Link to="/" className="text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] text-sm mt-2 inline-block">
                ← Back to home
              </Link>
            </div>

            {!selectedRole && !showOnboarding && (
              <div className="space-y-4">
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="pf-btn-primary w-full py-4 group"
                >
                  <div className="flex items-center justify-center gap-3">
                    <Sparkles className="w-6 h-6" />
                    <span>Start with Quick Questions</span>
                    <ArrowRight className="w-5 h-5 transform transition-transform group-hover:translate-x-1" />
                  </div>
                </button>
                
                <div className="text-center">
                  <p className="text-gray-600 mb-4">Answer 5 questions to get personalized recommendations</p>
                  <button
                    onClick={() => {
                      setSelectedRole('manual'); // Set a flag to show manual signup form
                      setShowOnboarding(false);
                    }}
                    className="text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium underline"
                  >
                    Skip questions, sign up directly
                  </button>
                </div>
              </div>
            )}

            {selectedRole && (
              <div className="pf-organic-card p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-red-600"></div>
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="pf-form-group">
                    <label htmlFor="name" className="pf-label">Full name</label>
                    <div className="input-icon-wrap">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                      <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className="pf-input" placeholder="Enter your full name" />
                    </div>
                  </div>

                  <div className="pf-form-group">
                    <label htmlFor="email" className="pf-label">Email address</label>
                    <div className="input-icon-wrap">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                      <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="pf-input" placeholder="Enter your email" />
                    </div>
                  </div>

                  <div className="pf-form-group">
                    <label htmlFor="phone" className="pf-label">Phone number</label>
                    <div className="input-icon-wrap">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                      <input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} className="pf-input" placeholder="Enter your phone number" />
                    </div>
                  </div>

                  <div className="pf-form-group">
                    <label htmlFor="role" className="pf-label">
                      I am a <span className="text-red-600">*</span>
                    </label>
                    <select id="role" name="role" required value={formData.role} onChange={handleChange} className="pf-input bg-white">
                      <option value="">Select your role...</option>
                      <option value="owner">Pet owner (looking for care)</option>
                      <option value="caregiver">Caregiver (providing care)</option>
                    </select>
                    {!formData.role && (
                      <p className="mt-1 text-xs text-[var(--pf-ink-muted)]">
                        Choose whether you want to find care for your pets or provide care services
                      </p>
                    )}
                  </div>

                  <div className="pf-form-group">
                    <label htmlFor="password" className="pf-label">Password</label>
                    <div className="input-icon-wrap relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                      <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={handleChange} className="pf-input pr-10" placeholder="Create a password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)]" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="pf-form-group">
                    <label htmlFor="confirmPassword" className="pf-label">Confirm password</label>
                    <div className="input-icon-wrap relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
                      <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.confirmPassword} onChange={handleChange} className="pf-input pr-10" placeholder="Confirm your password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)]" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    required
                    className="h-4 w-4 text-[var(--pf-accent)] focus:ring-[var(--pf-accent)] border-gray-300 rounded"
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
                    I agree to the{' '}
                    <Link to="/terms" className="text-[var(--pf-accent)] hover:underline">
                      Terms and Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-[var(--pf-accent)] hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="pf-btn-primary w-full justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                <div className="text-center space-y-3 pt-2">
                  <p className="text-gray-600">Already have an account?</p>
                  <Link to="/login" className="pf-btn-secondary pf-btn-nav no-underline">
                    Sign In
                  </Link>
                </div>
              </form>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Image */}
        <div className="hidden lg:flex pf-auth-visual relative w-1/2">
          <img src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=2000&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="pf-hero-v2__pattern absolute inset-0" />
          <div className="relative z-10 flex items-center justify-center p-12 w-full">
            <div className="max-w-md text-white text-center">
              <Logo to="/" size={48} variant="light" className="mx-auto mb-6" />
              <h2 className="pf-display text-4xl mb-6">Join PetsFostering Today</h2>
              <p className="text-xl text-white/80 mb-8">
                Connect with loving caregivers and give your pets the care they deserve.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl bg-white/10 backdrop-blur">
                  <div className="text-3xl font-bold mb-2">24/7</div>
                  <div className="text-white/70">Support</div>
                </div>
                <div className="p-6 rounded-2xl bg-white/10 backdrop-blur">
                  <div className="text-3xl font-bold mb-2">1000+</div>
                  <div className="text-white/70">Happy Pets</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={handleOnboardingComplete}
        userRole={null}
      />
    </>
  );
};

export default Signup;